// anagrafiche-api.js — amm-v3 (A2, ondata 29): parte PURA della vista GENERICA
// "Anagrafiche" — UNA vista per le 8 pagine che il v1 serve con
// renderAnagrafica(el, key) (r.4106-4267) — testabile sotto node (contract
// test server/src/lib/amm-v3-anagrafiche.test.js).
//
// PRINCIPI (rito A1/proc-v3):
//  - La CONFIGURAZIONE (titoli, colonne con getter HTML, etichetta '+ Nuovo…')
//    è DELEGATA ad AMM.anagConfig(): unica fonte di verità, label VERBATIM
//    per costruzione a runtime (il contract test pinna la firma e le keys).
//    I getter di colonna c.g(r) producono HTML v1 (già escapato da AMM.esc):
//    iniettato verbatim nelle celle, come i badge del Cruscotto A1.
//  - TESTI che il v3 ricompone (tab, bottoni riga, vuoti, toast, banner IMU):
//    VERBATIM dal v1, pinnati dal contract test.
//  - MODALI/SCHEDE DELEGATI: anagRowClick (libretto/dipendente/documento/
//    fascicolo atto), editAnagrafica (editor+B6), confirmDeleteAnagrafica,
//    confirmArchiviaAnagrafica, apriRegistraUtilizzoCredito, openImportBuste.
//    Il refresh post-save di editAnagrafica è GIÀ renderPage(key) (r.4455):
//    passa dal wrap, nessun interceptor necessario.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/** (asserito dai test).

import { apiPathFn } from './api.js';

// le 8 pagine servite da renderAnagrafica — dispatch v1 r.1101-1104
export const AMM_ANAG_KEYS = ['veicoli', 'polizze', 'documenti', 'dipendenti', 'atti', 'immobili', 'crediti', 'cantieri'];

// ---- metodi di window.AMM che il v3 DELEGA ----
export const AN_DELEGATED_METHODS = [
  'renderPage',              // il seam stesso (wrap) + refresh post-azione
  'renderAnagrafica',        // la vista v1 wrappata (drift guard)
  'anagConfig',              // titoli + colonne + '+ Nuovo…' (unica fonte: v1)
  'loadLookups',             // lookups per vista (RCA, tipi documento, IMU…)
  'anagMatchesFiltro',       // ricerca client-side (solo dipendenti)
  'anagRowClick',            // click riga → scheda dedicata v1 (libretto/dipendente/…)
  'editAnagrafica',          // editor generico v1 (B6 già dentro) — resta v1
  'confirmDeleteAnagrafica', // conferma informata + DELETE (v1)
  'confirmArchiviaAnagrafica', // conferma informata + archivia (v1)
  'apriRegistraUtilizzoCredito', // modale utilizzo credito (v1)
  'openImportBuste',         // wizard import buste AI (v1)
  'toast',
  'toastConLink',            // 'Archiviato — Vedi archiviati' (v1)
  'helpIcon',                // ⓘ nel titolo Documenti (HTML v1, delegation su document)
  'isAdmin',                 // elimina/archivia/riattiva solo admin (parità F15)
  'eur',                     // banner IMU (formattazione €, unica fonte: v1)
];

// ---- funzioni di window.api.amm usate DIRETTAMENTE dal v3 (le stesse del
// v1; condivise da TUTTE le 8 spec ⇒ install all-or-nothing) ----
export const AN_API_PATHS = AMM_ANAG_KEYS.map(function (k) { return k + '.list'; })
  // riattiva: usata solo dalle 3 pagine ARCHIVIABILI (veicoli/polizze/documenti)
  .concat(['veicoli.riattiva', 'polizze.riattiva', 'documenti.riattiva']);

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const AN_TEXTS = {
  tabAttiviPre: 'Attivi (',
  tabArchiviatiPre: 'Archiviati (',
  btnNuovoPre: '+ ',
  importaBuste: 'Importa buste paga (AI)',
  costiCantiere: 'Costi per cantiere',
  filtroDipPlaceholder: 'Cerca per cognome, nome o codice fiscale…',
  archivia: 'Archivia',
  riattiva: 'Riattiva',
  elimina: 'elimina',
  registraUtilizzo: 'Registra utilizzo',
  toastEliminato: 'Eliminato',
  toastArchiviato: 'Archiviato',
  vediArchiviati: 'Vedi archiviati',
  toastRiattivato: 'Riattivato',
  riattivaFallita: 'Riattivazione NON riuscita: ',
  emptyArchiviati: 'Nessun elemento archiviato.',
  emptyFiltro: 'Nessun risultato per il filtro.',
  emptyPre: 'Nessun elemento. Creane uno con "+ ',
  emptyPost: '".',
  imuPre: 'IMU ',
  imuCalcolata: ' (calcolata)',
  imuLink: 'Vedi nel Piano imposte ›',
};

// colori VERBATIM v1 (tab attivo/inattivo r.4207-4208)
export const AN_COLORS = { tabOn: '#0e7490', tabOff: '#64748b' };

// ---- FORMULE v1 replicate (pure) ----

// messaggio vuoto — r.4127: archiviati / filtro senza risultati / lista vuota
export function emptyMessageAnag(archivedView, listNonVuota, nuovoLabel) {
  if (archivedView) return AN_TEXTS.emptyArchiviati;
  return listNonVuota ? AN_TEXTS.emptyFiltro : AN_TEXTS.emptyPre + nuovoLabel + AN_TEXTS.emptyPost;
}

// nome mostrato nella conferma di delete — r.4160/4415: solo dipendenti
export function nomeDipendente(key, rec) {
  return key === 'dipendenti' ? [rec.cognome, rec.nome].filter(Boolean).join(' ') : undefined;
}

// 'Registra utilizzo' — r.4141: crediti, vista attivi, non esaurito
export function mostraRegistraUtilizzo(key, r, archivedView) {
  return key === 'crediti' && !archivedView && r.stato !== 'esaurito';
}

// ---- LETTURA dati (stessa composizione del v1 r.4115-4119: lista + lookups
// + archiviati SOLO se la pagina è archiviabile, in Promise.all) ----
export async function readAnagrafica(win, key, archiviabile) {
  const res = await Promise.all([
    apiPathFn(win, key + '.list').call(null),
    win.AMM.loadLookups(key),
    archiviabile ? apiPathFn(win, key + '.list').call(null, true) : Promise.resolve([]),
  ]);
  return { list: res[0] || [], lookups: res[1] || {}, listArchiviati: res[2] || [] };
}

// ---- AZIONI (stessa API del v1) ----
// riattiva — r.4182: POST /<key>/:id/riattiva
export function riattivaAnag(win, key, id) {
  return apiPathFn(win, key + '.riattiva').call(null, id);
}
// refresh post-azione = STESSA via del v1 (renderPage(currentPage)): passa dal wrap
export function refreshPagina(win) { return win.AMM.renderPage(win.AMM.currentPage); }
// link IMU → pagina Imposte: stessa via del v1 (r.4248-4249) — click sulla
// voce sidebar se esiste (bridge/nav coerenti), altrimenti renderPage diretto
export function vaiATributi(win) {
  const navEl = win.document.querySelector('.amm-link[data-page="tributi"]');
  if (navEl) navEl.click(); else win.AMM.renderPage('tributi');
}
